import { useState, useRef, useEffect, type JSX } from 'react'
import { useNavigate } from 'react-router'
import { Bell, CheckCheck } from 'lucide-react'
import { api } from '../../api/client.js'
import { useClickOutside } from '../../hooks/useClickOutside.js'
import {
  useNotificationsStore,
  type NotificationItem,
} from '../../store/notificationsStore.js'
import { requestNotificationPermissionOnce } from '../../utils/browserNotifications.js'
import { playNotificationChime } from '../../utils/notificationSound.js'
import { timestampMs } from '../../utils/timezone.js'

function timeAgo(iso: string): string {
  const then = timestampMs(iso)
  if (Number.isNaN(then)) return ''
  const diff = Math.max(0, Date.now() - then)
  const mins = Math.floor(diff / 60_000)
  if (mins < 1) return 'just now'
  if (mins < 60) return `${mins}m ago`
  const hrs = Math.floor(mins / 60)
  if (hrs < 24) return `${hrs}h ago`
  const days = Math.floor(hrs / 24)
  return `${days}d ago`
}

export default function NotificationBellWidget({ disabled = false }: { disabled?: boolean }): JSX.Element {
  const [open, setOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const [loadingMore, setLoadingMore] = useState(false)
  const [hasMore, setHasMore] = useState(false)
  const [offset, setOffset] = useState(0)
  const ref = useRef<HTMLDivElement>(null)
  const navigate = useNavigate()
  useClickOutside(ref, () => setOpen(false), open)

  const notifications = useNotificationsStore((s) => s.notifications)
  const unreadCount = useNotificationsStore((s) => s.unreadCount)
  const lastLiveId = useNotificationsStore((s) => s.lastLiveId)

  // Guard so the chime never plays for the very first store update this
  // widget instance observes (e.g. remounts on a live id already set from a
  // prior mount) — only a lastLiveId change that occurs strictly after mount
  // should play it.
  const hasMountedRef = useRef(false)

  // Mount: prime the unread badge and request native notification permission
  // once. The list stays empty until the panel is opened.
  // Live count updates come via WebSocket (notification:new event).
  useEffect(() => {
    let cancelled = false
    async function loadCount(): Promise<void> {
      try {
        const res = await api.get<{ count: number }>('/notifications/unread-count')
        if (cancelled) return
        useNotificationsStore.getState().setInitial([], res.count)
      } catch {
        // Leave the badge at its current value on error.
      } finally {
        if (!cancelled) hasMountedRef.current = true
      }
    }
    void loadCount()
    requestNotificationPermissionOnce()
    return () => {
      cancelled = true
    }
  }, [])

  // Play the chime only when a live socket push arrives (addLive bumps
  // lastLiveId) — setInitial (mount-time badge fetch, open-time list fetch)
  // never touches lastLiveId, so opening/reopening the panel cannot trigger
  // this, unlike watching the notifications array identity directly.
  useEffect(() => {
    if (!hasMountedRef.current) return
    if (!lastLiveId) return
    playNotificationChime()
  }, [lastLiveId])

  // Panel open: fetch the first page of the list. unreadCount is NOT derived
  // from this page — it stays driven by /notifications/unread-count (mount
  // fetch) and live socket pushes, since the list here may be a small subset
  // of all unread notifications.
  useEffect(() => {
    if (!open) return
    let cancelled = false
    async function loadList(): Promise<void> {
      setLoading(true)
      try {
        const res = await api.get<{ notifications: NotificationItem[]; hasMore: boolean; total: number }>('/notifications?offset=0&limit=10')
        if (cancelled) return
        useNotificationsStore.getState().setNotifications(res.notifications ?? [])
        setHasMore(res.hasMore ?? false)
        setOffset(10)
      } catch {
        // Keep whatever is already in the store on error.
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    void loadList()
    return () => {
      cancelled = true
    }
  }, [open])

  async function handleClick(n: NotificationItem): Promise<void> {
    console.log('[DEBUG NotificationBellWidget] handleClick, notification:', n)
    if (!n.isRead) {
      try {
        await api.patch(`/notifications/${n.id}/read`)
      } catch {
        // Optimistically mark read regardless; the badge polling recovers.
      }
      useNotificationsStore.getState().markRead(n.id)
    }
    if (n.conversationId) {
      setOpen(false)
      // A still-staged Live Chat (live_chats.id, pre "Create Conversation")
      // shares the conversationId field with finalized conversations.id —
      // the inbox route only resolves the latter and 404s on the former.
      if (n.entityType === 'live_chat') {
        navigate(`/agent/chat?conversationId=${n.conversationId}&channel=chat`)
      } else {
        console.log('[DEBUG NotificationBellWidget] routing to inbox, entityType was:', n.entityType)
        navigate(`/agent/conversation/inbox?scope=all&conversationId=${n.conversationId}`)
      }
    }
  }

  async function handleMarkAllRead(): Promise<void> {
    try {
      await api.post('/notifications/read-all')
    } catch {
      // Optimistically clear regardless.
    }
    useNotificationsStore.getState().markAllRead()
  }

  async function handleLoadMore(): Promise<void> {
    setLoadingMore(true)
    try {
      const res = await api.get<{ notifications: NotificationItem[]; hasMore: boolean }>(`/notifications?offset=${offset}&limit=10`)
      useNotificationsStore.getState().appendNotifications(res.notifications ?? [])
      setHasMore(res.hasMore ?? false)
      setOffset((prev) => prev + 10)
    } catch {
      // Ignore error - user can try again
    } finally {
      setLoadingMore(false)
    }
  }

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        disabled={disabled}
        className={`relative flex h-8 w-8 items-center justify-center rounded-lg transition-colors ${
          disabled
            ? 'cursor-not-allowed text-gray-300'
            : 'text-gray-500 hover:bg-gray-100'
        }`}
        aria-label={`${unreadCount} unread notification${unreadCount === 1 ? '' : 's'}`}
        aria-expanded={open}
        onClick={() => { if (!disabled) setOpen((v) => !v) }}
      >
        <Bell className="h-4 w-4" aria-hidden />
        {unreadCount > 0 && (
          <span className="absolute -right-1 -top-1 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold text-white">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>
      {open && (
        <div className="absolute right-0 z-50 mt-1 w-80 rounded-xl border border-gray-200 bg-white py-1 shadow-lg">
          <div className="flex items-center justify-between px-3 py-2">
            <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-gray-400">
              <Bell className="h-3 w-3" />
              Notifications
            </div>
            {unreadCount > 0 && (
              <button
                type="button"
                onClick={() => void handleMarkAllRead()}
                className="flex items-center gap-1 text-[11px] font-medium text-blue-600 hover:text-blue-700"
              >
                <CheckCheck className="h-3 w-3" />
                Mark all read
              </button>
            )}
          </div>

          <div className="mx-3 border-t border-gray-100" />

          <div className="max-h-96 overflow-y-auto">
            {loading ? (
              <div className="p-4 text-xs text-gray-400">Loading notifications…</div>
            ) : notifications.length === 0 ? (
              <div className="p-4 text-center">
                <Bell className="mx-auto mb-2 h-8 w-8 text-gray-300" />
                <p className="text-sm font-medium text-gray-600">No notifications</p>
                <p className="text-xs text-gray-400">You're all caught up.</p>
              </div>
            ) : (
              <>
                {notifications.map((n) => (
                  <button
                    key={n.id}
                    type="button"
                    onClick={() => void handleClick(n)}
                    className={`flex w-full items-start gap-2 px-3 py-2.5 text-left transition-colors hover:bg-gray-50 ${
                      n.isRead ? '' : 'bg-blue-50/40'
                    }`}
                  >
                    <span
                      className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${
                        n.isRead ? 'bg-transparent' : 'bg-blue-500'
                      }`}
                      aria-hidden
                    />
                    <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                      <span className="truncate text-[13px] font-semibold text-gray-800">
                        {n.title}
                      </span>
                      {n.description && (
                        <span className="line-clamp-2 text-xs text-gray-500">
                          {n.description}
                        </span>
                      )}
                      {(n.actorName || n.assigneeName) && (
                        <div className="flex flex-wrap gap-x-2 gap-y-0.5 text-[10px] text-gray-400">
                          {n.actorName && (
                            <span>By {n.actorName}</span>
                          )}
                          {n.actorName && n.assigneeName && (
                            <span aria-hidden>•</span>
                          )}
                          {n.assigneeName && (
                            <span>Assigned to {n.assigneeName}</span>
                          )}
                        </div>
                      )}
                      <div className="flex items-center gap-2 text-[10px] text-gray-400">
                        <span>{timeAgo(n.createdAt)}</span>
                        {n.campaignName && (
                          <>
                            <span aria-hidden>•</span>
                            <span className="truncate">{n.campaignName}</span>
                          </>
                        )}
                      </div>
                    </div>
                  </button>
                ))}
                {hasMore && (
                  <button
                    type="button"
                    onClick={() => void handleLoadMore()}
                    disabled={loadingMore}
                    className="w-full py-2 text-xs font-medium text-blue-600 hover:bg-gray-50 disabled:text-gray-400 disabled:cursor-not-allowed"
                  >
                    {loadingMore ? 'Loading...' : 'Load more'}
                  </button>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
